// PostHog analytics tracking
import posthog from 'posthog-js';

// Initialize PostHog
export function initPostHog() {
  if (typeof window !== 'undefined' && !posthog.__loaded) {
    posthog.init(
      'phc_D5EHD7FLNbQxIUCd37WT6DK38MAQNErOsudg1qT7n7q',
      {
        api_host: 'https://us.i.posthog.com',
        person_profiles: 'identified_only',
        capture_pageview: true,
        capture_pageleave: true,
        autocapture: true,
      }
    );
  }
}

// Track events - all info-level tracking goes through here
export const trackEvent = {
  // === Customer flow events ===
  feedbackPageViewed: (locationId: string) => {
    posthog.capture('feedback_page_viewed', { location_id: locationId });
  },

  feedbackSubmitted: (locationId: string, positive: boolean, timeToDecisionSeconds?: number) => {
    posthog.capture('feedback_submitted', { 
      location_id: locationId, 
      positive,
      sentiment: positive ? 'positive' : 'negative',
      time_to_decision_seconds: timeToDecisionSeconds,
    });
  },

  feedbackAbandoned: (locationId: string, timeOnPageSeconds: number) => {
    posthog.capture('feedback_abandoned', { 
      location_id: locationId,
      time_on_page_seconds: timeOnPageSeconds,
    });
  },

  reviewSiteShown: (locationId: string, shownPlatform: string, availablePlatforms: string[]) => {
    posthog.capture('review_site_shown', { 
      location_id: locationId,
      shown_platform: shownPlatform,
      available_platforms: availablePlatforms,
      available_count: availablePlatforms.length,
    });
  },

  reviewSiteClicked: (locationId: string, platform: string) => {
    posthog.capture('review_site_clicked', { 
      location_id: locationId, 
      platform,
    });
  },

  whatsappOpened: (locationId: string) => {
    posthog.capture('whatsapp_opened', { location_id: locationId });
  },

  // === Business flow events ===
  businessSignedUp: (businessId: string, email: string) => {
    posthog.capture('business_signed_up', { business_id: businessId });
    posthog.identify(businessId, { email });
  },

  businessLoggedIn: (businessId: string) => {
    posthog.capture('business_logged_in', { business_id: businessId });
    posthog.identify(businessId);
  },

  qrCodeDownloaded: (locationId: string) => {
    posthog.capture('qr_code_downloaded', { location_id: locationId });
  },

  settingsUpdated: (businessId: string, fields: string[]) => {
    posthog.capture('settings_updated', { 
      business_id: businessId, 
      fields,
      fields_count: fields.length,
    });
  },

  // === Billing events ===
  paymentCompleted: (businessId: string, method: 'stripe' | 'oxxo' | 'paypal' | 'cash', amount: number) => {
    posthog.capture('payment_completed', { 
      business_id: businessId, 
      method, 
      amount,
      currency: 'MXN',
    });
  },

  // === Onboarding events ===
  onboardingStarted: (businessId: string) => {
    posthog.capture('onboarding_started', { business_id: businessId });
  },

  onboardingStepCompleted: (businessId: string, step: string, fieldsFilled?: string[], fieldsSkipped?: string[]) => {
    posthog.capture('onboarding_step_completed', { 
      business_id: businessId, 
      step,
      fields_filled: fieldsFilled || [],
      fields_skipped: fieldsSkipped || [],
      fields_filled_count: fieldsFilled?.length || 0,
      fields_skipped_count: fieldsSkipped?.length || 0,
    });
  },

  onboardingCompleted: (businessId: string, config?: { has_google: boolean; has_facebook: boolean; has_whatsapp: boolean; review_sites_count: number }) => {
    posthog.capture('onboarding_completed', { 
      business_id: businessId,
      ...config,
    });
  },
};

// Track warnings (not errors, but worth monitoring)
export const trackWarning = {
  apiRequestFailed: (endpoint: string, status: number, message?: string) => {
    posthog.capture('api_request_failed', {
      endpoint,
      status,
      message,
      severity: 'warning',
    });
  },

  validationFailed: (field: string, reason: string) => {
    posthog.capture('validation_failed', {
      field,
      reason,
      severity: 'warning',
    });
  },

  sessionExpired: () => {
    posthog.capture('session_expired', { severity: 'warning' });
  },
};

// Track errors (things that need attention)
export const trackError = {
  unexpected: (source: string, message: string, metadata?: Record<string, any>) => {
    posthog.capture('$exception', {
      $exception_type: 'unexpected_error',
      $exception_message: message,
      $exception_source: source,
      severity: 'error',
      ...metadata,
    });
  },

  networkError: (endpoint: string, error: string) => {
    posthog.capture('$exception', {
      $exception_type: 'network_error',
      $exception_message: error,
      $exception_source: endpoint,
      severity: 'error',
    });
  },
};

export default posthog;
